import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, TextField, Typography, Button } from '@mui/material';
import { AskedBadge, Eyebrow, SeasonBadge, StatStrip } from './Badges';
import OrderLineCard from './OrderLineCard';
import BlockedSection from './BlockedSection';
import HeldBackSection from './HeldBackSection';
import OrderAccounting from './OrderAccounting';
import AssortmentOrderOverview from './AssortmentOrderOverview';
import UnaccountedSection from './UnaccountedSection';
import OutletSection from './OutletSection';
import UnsourcedSection from './UnsourcedSection';
import HiddenFold from './HiddenFold';
import { SinsangBatch, SinsangNotice, Sourcing } from './SinsangLinks';
import {
  WHY_STATUS_LABEL, asksSummary, displayTitle, monthDay, orderPieces, tierLines, warehouseSummary,
  weekdayLabel,
} from './format';
import {
  RestockV2Addable, RestockV2FlatLine, SHOP_NOTE_HINT, addableVariants, catalogVariantIndex,
  flatOrderLines, includedLines, isExcluded, koreaOrderLines, koreaOrderedLines, koreaTotalKept,
  laneShortLine, orderCopyText, selectionSummary, shopNoteTags, shopTag, styleOwnedLine,
  tierTotalKept, variantStockIndex,
} from './orderPlan';
import { excludedStyles } from './exclusions';
import {
  OrderEdit, isAddedVariant, isOmitted, lineIsEdited, lineQty, orderEditLine, variantQty,
} from './orderEdit';
import { C, MONO, RADIUS, SHADOW, TIER_BADGE, TIER_ORDER, hiddenFoldLabel } from './tokens';
import {
  RestockV2CounterOrder, RestockV2Excluded, RestockV2OrderGroup, RestockV2OrderLine,
  RestockV2OrderVariant, RestockV2Plan, RestockV2ThumbTarget, RestockV2Why,
} from './types';

interface OrderScreenProps {
  plan: RestockV2Plan;
  busy: boolean;
  orderPending?: boolean;
  why: RestockV2Why | null;
  whyLoading: boolean;
  whyError: string;
  // Receipt for the latest explicitly created order.
  counterOrder: RestockV2CounterOrder | null;
  orderUncertain: boolean;
  savedOrderText?: string | null;
  onCreateOrder: () => void;
  onNextOrder: () => void;
  // SKU → true for the lines 不要這款 was just tapped on and the server has not recomputed yet. The
  // PERSISTED vetoes are not in here at all: they are gone from order.groups and listed in
  // plan.excluded.plan instead, which is what the 已排除 fold below renders.
  excluded: RestockV2Excluded;
  // Veto keys currently in flight, so a line disables only its own control.
  excludePending: Record<string, boolean>;
  // This order's own edits: which lines he unticked and any quantity he set himself.
  edit: OrderEdit;
  sourcing: Sourcing;
  // ≥1024 px: the store groups on the left, one sticky summary panel on the right.
  desktop: boolean;
  wide: boolean;
  // 這次不訂 — local, instant, writes nothing.
  onToggleOrder: (sku: string) => void;
  // 全部取消／全部恢復 over the rows currently on screen. `omitted` only — see orderEdit.ts.
  onOmitAll: (skus: string[], omit: boolean) => void;
  // 不要這款 — the standing veto, shared with Adam.
  onVeto: (sku: string) => void;
  onVariantQty: (sku: string, variant: RestockV2OrderVariant, next: number) => void;
  onResetLine: (line: RestockV2OrderLine) => void;
  // 加顏色／尺寸 — a colour the engine never put on the line. Local, like every other order edit.
  onAddVariant: (sku: string, row: RestockV2Addable) => void;
  onRemoveVariant: (sku: string, variantId: number) => void;
  onAddBack: (sku: string) => void;
  // Copy is read-only; only onCreateOrder opens a receiving order.
  onCopy: (text: string) => void;
  // Cancel the latest unreceived order.
  onUndoOrder: () => void;
  onWhy: (sku: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
  // 出清架 — a lot was just transferred into the office, so the shelf AND the office both moved.
  // Recompute immediately rather than on the debounce: the piece has physically changed hands.
  onOutletPulled: () => void;
}

// Everything the 加顏色／尺寸 picker needs, bundled so the two group call sites cannot drift apart.
interface VariantPicker {
  addableOf: (line: RestockV2OrderLine) => RestockV2Addable[];
  catalogKnown: (sku: string) => boolean;
  ownedOf: (sku: string) => string;
  open: Record<string, boolean>;
  onToggle: (key: string) => void;
  onAdd: (sku: string, row: RestockV2Addable) => void;
  onRemove: (sku: string, variantId: number) => void;
}

// Everything a card needs that is the SAME for every card on the screen. Bundled so the flat Korea
// list and the 台灣倉庫 section cannot drift apart in how they wire one.
interface CardDeck {
  excluded: RestockV2Excluded;
  excludePending: Record<string, boolean>;
  edit: OrderEdit;
  sourcing: Sourcing;
  picker: VariantPicker;
  whyOpen: Record<string, boolean>;
  onWhy: (key: string) => void;
  onToggleOrder: (sku: string) => void;
  onVeto: (sku: string) => void;
  onVariantQty: (sku: string, variant: RestockV2OrderVariant, next: number) => void;
  onResetLine: (line: RestockV2OrderLine) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const OrderCards: React.FC<{
  keyspace: string; rows: RestockV2FlatLine[]; columns: number; deck: CardDeck;
}> = ({ keyspace, rows, columns, deck }) => {
  const { excluded, excludePending, edit, sourcing, picker, whyOpen } = deck;
  return (
    <Box
      // `alignItems: start` because a card can grow a lot taller than its neighbour when the
      // 加顏色／尺寸 picker is open; stretched grid cells gave the quiet card a half-screen of empty
      // white beside the open one.
      sx={columns > 1 ? {
        display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 1,
        alignItems: 'start',
      } : undefined}
    >
      {rows.map(({ line, shop }) => {
        const key = `${keyspace}:${line.sku}`;
        return (
          <OrderLineCard
            key={key} line={line} shop={shop} sourcing={sourcing}
            // `line` already carries HIS quantities (applyOrderEdit ran on the plan), so the card's
            // numbers, the totals above it and the copy text are the same numbers by construction.
            excluded={isExcluded(excluded, line.sku) && !isOmitted(edit, line.sku)}
            omitted={isOmitted(edit, line.sku)}
            pending={!!excludePending[line.sku]}
            qtyOf={(variant) => variantQty(edit, line.sku, variant)}
            edited={lineIsEdited(edit, line)} pieces={lineQty(edit, line)}
            addable={picker.addableOf(line)} catalogKnown={picker.catalogKnown(line.sku)}
            ownedLine={picker.ownedOf(line.sku)}
            addedOf={(variant) => isAddedVariant(edit, line.sku, variant.variant_id)}
            pickerOpen={!!picker.open[key]}
            onTogglePicker={() => picker.onToggle(key)}
            onAddVariant={picker.onAdd} onRemoveVariant={picker.onRemove}
            onToggleOrder={deck.onToggleOrder} onVeto={deck.onVeto} onVariantQty={deck.onVariantQty}
            onResetLine={() => deck.onResetLine(line)}
            whyOpen={!!whyOpen[key]} onWhy={() => deck.onWhy(key)}
            onImage={deck.onImage}
          />
        );
      })}
    </Box>
  );
};

// 台灣倉庫 keeps its heading. It is not a 進貨店家 — it is a different LANE (stock Magnus already owns
// in Taiwan, 不用等船, never ordered from Korea and carried separately in the copy text's 倉庫另補).
// Flattening it into the Korea list would be the one merge that actually loses information.
const WarehouseGroup: React.FC<{
  label: string; note: string; pieces: number; rows: RestockV2FlatLine[]; columns: number; deck: CardDeck;
}> = ({ label, note, pieces: qty, rows, columns, deck }) => (
  <Box component="section" sx={{ mb: 1.5 }}>
    <Box
      aria-label={`${label} ${qty} 件`}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.9 }}
    >
      <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em' }}>
        {label}
        <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '4px' }}>{note}</Box>
      </Typography>
      <Typography
        sx={{
          ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.ink2,
          bgcolor: C.paper2, borderRadius: '99px', px: 1,
        }}
      >
        {qty} 件
      </Typography>
    </Box>
    <OrderCards keyspace={label} rows={rows} columns={columns} deck={deck} />
  </Box>
);

const OrderScreen: React.FC<OrderScreenProps> = ({
  plan, busy, orderPending = false, why, whyLoading, whyError, counterOrder, excluded, excludePending, edit, sourcing,
  desktop, wide, onToggleOrder, onOmitAll, onVeto, onVariantQty, onResetLine, onAddVariant, onRemoveVariant,
  onAddBack, onCopy, onCreateOrder, onNextOrder, orderUncertain, savedOrderText, onUndoOrder, onWhy, onImage, onOutletPulled,
}) => {
  const [skuInput, setSkuInput] = useState('');
  const [openWhy, setOpenWhy] = useState<Record<string, boolean>>({});
  const [openPicker, setOpenPicker] = useState<Record<string, boolean>>({});
  const order = plan.order;
  const meta = plan.meta;
  const orderedLines = koreaOrderedLines(plan, excluded);
  const warehouseKept = includedLines(order?.warehouse, excluded);
  const warehouseLine = warehouseSummary(warehouseKept);
  const groups: RestockV2OrderGroup[] = order?.groups || [];
  const asks = asksSummary(meta?.autumn?.asks);
  const laneShort = laneShortLine(meta?.autumn);
  const selection = selectionSummary(order, excluded);
  // 不要這款 is now server state, so the styles taken out are not in `order` any more.
  const vetoed = excludedStyles(plan);
  const copyText = savedOrderText || orderCopyText(plan, excluded);
  const editLine = orderEditLine(edit);
  // The rows the bulk control acts on: what is ON SCREEN, never the whole plan. Cancelling rows
  // he cannot see is how a bulk control earns distrust, and this screen removed one such control
  // already (2026-09-15).
  const shownSkus = groups.flatMap((group) => (group.lines || []).map((line) => line.sku));
  const allOmitted = shownSkus.length > 0 && shownSkus.every((sku) => isOmitted(edit, sku));
  const koreaKept = koreaTotalKept(order, excluded);

  const lookup = () => { const sku = skuInput.trim(); if (sku) onWhy(sku); };
  const toggleWhy = (key: string) => setOpenWhy((old) => ({ ...old, [key]: !old[key] }));
  const cardColumns = desktop ? 2 : 1;

  // Built ONCE per plan, not per card: `variantStockIndex` walks the whole plan, and a 40-line
  // order would otherwise walk it 40 times on every keystroke of a stepper.
  const catalog = useMemo(() => catalogVariantIndex(plan), [plan]);
  const stock = useMemo(() => variantStockIndex(plan), [plan]);
  const picker = useMemo(() => ({
    addableOf: (line: RestockV2OrderLine) => addableVariants(line, catalog, stock),
    catalogKnown: (sku: string) => catalog[sku] !== undefined,
    ownedOf: (sku: string) => styleOwnedLine(plan, sku),
    open: openPicker,
    onToggle: (key: string) => setOpenPicker((old) => ({ ...old, [key]: !old[key] })),
    onAdd: onAddVariant,
    onRemove: onRemoveVariant,
  }), [plan, catalog, stock, openPicker, onAddVariant, onRemoveVariant]);

  const deck: CardDeck = {
    excluded, excludePending, edit, sourcing, picker, whyOpen: openWhy, onWhy: toggleWhy,
    onToggleOrder, onVeto, onVariantQty, onResetLine, onImage,
  };

  // ONE list of cards, sorted 主力 → 新季款 → 線索款 → 分類 → SKU. No shop headings: the shop is a
  // chip on the card (Magnus 2026-09-19, 「don't need to order by tag name」).
  const flat = useMemo(() => flatOrderLines(order), [order]);
  const warehouseRows: RestockV2FlatLine[] = (order?.warehouse || [])
    .map((line) => ({ line, shop: shopTag(line.source_tag) }));
  const noteTags = shopNoteTags(order);

  // 批次開啟 Sinsang, kept but moved. A per-shop button was meaningless when 17 of 20 shops held one
  // line; it is genuinely useful when a shop holds several, which is how he actually buys. So: ONE
  // whole-order control (it opens 10 at a time and remembers what it opened, so pressing it again
  // walks the rest), plus a per-shop button ONLY where that shop has 2+ lines still ticked. A
  // single-line shop loses nothing — its 下單連結 is on its own card.
  const shopBatches = (order?.groups || [])
    .map((group) => ({
      shop: shopTag(group.source_tag),
      skus: includedLines(group.lines, excluded).map((line) => line.sku),
    }))
    .filter((row): row is { shop: string; skus: string[] } => !!row.shop && row.skus.length > 1);
  const allSkus = includedLines(koreaOrderLines(order), excluded).map((line) => line.sku);
  const sinsangBar = sourcing.allowed && sourcing.status === 'ready' && allSkus.length > 0 ? (
    <Box
      aria-label="批次開啟 Sinsang"
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.5 }}
    >
      <SinsangBatch sourcing={sourcing} label="全部" skus={allSkus} />
      {shopBatches.map((row) => (
        <SinsangBatch key={row.shop} sourcing={sourcing} label={row.shop} name={row.shop} skus={row.skus} />
      ))}
    </Box>
  ) : null;

  const tierTiles = (
    <Box sx={{ display: 'flex', gap: 0.9, mb: 1.5 }}>
      {TIER_ORDER.map((tier) => {
        const badge = TIER_BADGE[tier];
        const styles = includedLines(tierLines(order, tier), excluded).length;
        const total = tierTotalKept(order, tier, excluded);
        return (
          <Box
            key={tier}
            aria-label={`${badge.short} ${total} 件 · ${styles} 款`}
            sx={{
              flex: 1, bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: '10px',
              px: 1, py: 0.9, boxShadow: SHADOW, minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: badge.colour }}>{badge.short}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 19, fontWeight: 700, letterSpacing: '-.02em' }}>
              {total}
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.ink3, lineHeight: 1.3 }}>{styles} 款</Typography>
          </Box>
        );
      })}
    </Box>
  );

  // 「已選 N 款 / M 件」. There is deliberately no 全選 / 全部取消 any more: 不要這款 writes a shared
  // veto per style, so a single mis-tap on a bulk control would have vetoed the whole order for Adam
  // too. Taking a style out is one tap on its own line, and 加回 is one tap in the fold.
  const selectionBar = (
    <Box
      aria-label="已選"
      sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5, px: 1, py: 0.75,
        bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: '10px', boxShadow: SHADOW,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
        已選 <Box component="span" sx={{ fontFamily: MONO }}>{selection.styles}</Box> 款 ／
        {' '}<Box component="span" sx={{ fontFamily: MONO }}>{selection.pieces}</Box> 件
        {vetoed.length > 0 && (
          <Box component="span" sx={{ color: C.back, fontWeight: 700, fontSize: 12, ml: '5px' }}>
            已排除 {vetoed.length} 款
          </Box>
        )}
      </Typography>
      {/* What HE changed about this order, said in his own terms and kept apart from the standing
          排除 count — they are different decisions with different lifetimes. */}
      {editLine && (
        <Typography sx={{ fontSize: 12, color: C.wait, fontWeight: 700 }}>{editLine}</Typography>
      )}
      {/* 全部取消 — Magnus 2026-09-20: 「make a button that will allow me to deselect all」.
          It writes `omitted` (這次不訂), NOT the shared 不要這款 veto whose bulk control was
          removed on 2026-09-15 for vetoing Adam's order too. So the worst a mis-tap costs is the
          same tap again: the button flips to 全部恢復 the moment anything is off, which is why
          there is one control here and not two. Scoped to the rows on screen. */}
      {shownSkus.length > 0 && (
        <Box
          component="button" type="button" data-omit-all
          onClick={() => onOmitAll(shownSkus, !allOmitted)}
          aria-label={allOmitted ? `全部恢復 ${shownSkus.length} 款` : `全部取消 ${shownSkus.length} 款`}
          sx={{
            ml: 'auto', appearance: 'none', cursor: 'pointer', border: '1px solid',
            borderColor: allOmitted ? C.go : C.line, bgcolor: allOmitted ? C.goWash : C.card,
            color: allOmitted ? C.go : C.ink2, borderRadius: '8px', px: '9px', py: '3px',
            fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
            '&:hover': { borderColor: allOmitted ? C.go : C.back, color: allOmitted ? C.go : C.back },
          }}
        >
          {allOmitted ? '全部恢復' : '全部取消'}
        </Box>
      )}
    </Box>
  );

  const asksLine = asks ? (
    <Typography sx={{ fontSize: 12, color: C.ink2, mb: 1.5 }}>
      客人問過：{asks}（新季款的排序照這個加權）
    </Typography>
  ) : null;

  // The engine says what it could not find rather than pretending the target was met.
  const laneShortLineNode = laneShort ? (
    <Typography
      role="note"
      sx={{
        fontSize: 12.5, color: C.wait, fontWeight: 700, bgcolor: C.waitWash, borderRadius: '8px',
        px: 1, py: 0.6, mb: 1.5,
      }}
    >
      {laneShort}
    </Typography>
  ) : null;

  const groupsBlock = (
    <>
      <SinsangNotice sourcing={sourcing} />
      {sinsangBar}
      {groups.length === 0 && (
        <Typography sx={{ fontSize: 13, color: C.ink2, mb: 2 }}>這次沒有要下的韓國單。</Typography>
      )}
      {/* 「訂貨清單」 and not 「韓國訂貨 N 款」: that exact wording is already an OrderAccounting
          bucket's accessible name, and two of them is a screen with two things called the same. */}
      <Box component="section" aria-label="訂貨清單" sx={{ mb: 1.5 }}>
        <OrderCards keyspace="order" rows={flat} columns={cardColumns} deck={deck} />
      </Box>
      {(order?.warehouse || []).length > 0 && (
        <WarehouseGroup
          label="台灣倉庫" note="不用等船" pieces={orderPieces(warehouseKept)}
          rows={warehouseRows} columns={cardColumns} deck={deck}
        />
      )}
      {/* 出清架 before 訂不到: it is supply, and the Korea numbers above are already net of it. */}
      <OutletSection order={order} enabled={!busy} onSaved={onOutletPulled} onImage={onImage} />
      <UnsourcedSection order={order} onImage={onImage} />
      {/* Approved, wanted, and not orderable in any colour — `groups` cannot carry it. */}
      <BlockedSection order={order} onImage={onImage} />
      {/* 2026-10-04: 秋款先不加 (AW ceiling) and 下單超過 7 天未到 (stale open lines). */}
      <HeldBackSection order={order} onImage={onImage} />
      {/* Nothing on a healthy plan. When it is not nothing, a style stopped reaching every list
          and the screen it vanished from is where that gets said. */}
      <UnaccountedSection order={order} onImage={onImage} />
      {/* A DATA problem, reported where the other honest admissions live rather than above the
          first order card — it is not something he does before ordering, and on the phone the
          sentence cost five lines of the first screen. The note itself is never touched: it is on
          its card, verbatim (OrderLineCard's shop chip). */}
      {noteTags.length > 0 && (
        <Typography sx={{ fontSize: 11.5, color: C.ink3, lineHeight: 1.5, mb: 1.25 }}>
          {SHOP_NOTE_HINT}
          <Box component="span" sx={{ fontWeight: 800, color: C.ink2 }}>{noteTags.join('、')}</Box>
        </Typography>
      )}
      {/* The styles 不要這款 took out — still listed, with who and when, and 加回 in one tap. */}
      <HiddenFold
        label={hiddenFoldLabel('plan', vetoed.length)} styles={vetoed} pending={excludePending}
        onAddBackStyle={onAddBack}
      />
    </>
  );

  const whyBlock = (
    <>
      <Eyebrow>某一款為什麼沒在清單裡</Eyebrow>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small" fullWidth value={skuInput} onChange={(event) => setSkuInput(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') lookup(); }}
          placeholder="輸入 SKU"
          inputProps={{ 'aria-label': '查詢某款為什麼沒在清單裡' }}
        />
        <Button variant="outlined" disabled={whyLoading || !skuInput.trim()} onClick={lookup}>查</Button>
      </Box>
      {whyLoading && <Box role="status" sx={{ pt: 1.5 }}><CircularProgress size={18} /></Box>}
      {whyError && <Alert severity="error" sx={{ mt: 1.5 }}>{whyError}</Alert>}
      {why && !whyLoading && (
        <Box
          sx={{
            mt: 1.5, p: 1.5, borderRadius: `${RADIUS}px`, bgcolor: C.card, border: '1px solid',
            borderColor: C.line, boxShadow: SHADOW,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>{why.sku}</Typography>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 800 }}>{displayTitle(why.title)}</Typography>
            <SeasonBadge value={why.season} />
            <Typography
              component="span"
              sx={{ fontSize: 12, fontWeight: 800, bgcolor: C.paper2, color: C.ink2, borderRadius: '99px', px: '7px' }}
            >
              {WHY_STATUS_LABEL[why.status] || why.status}
            </Typography>
            <AskedBadge asked={why.asked} />
          </Box>
          {(why.lines || []).map((text, index) => (
            <Typography key={`${index}:${text}`} sx={{ fontSize: 13, color: C.ink2 }}>{text}</Typography>
          ))}
          {why.lane_reason && <Typography sx={{ fontSize: 13, color: C.ink3, mt: 0.5 }}>{why.lane_reason}</Typography>}
        </Box>
      )}
    </>
  );

  // What the one button says it will do, said before it is pressed rather than after. 「按下去會發生
  // 什麼」 is the whole difference between this and a button that silently opened an order.
  const orderPromise = orderedLines.length > 0
    ? `確認建立後會開一張門市訂單（${orderedLines.length} 筆 / ${orderedLines.reduce((sum, row) => sum + row.qty, 0)} 件），出貨人員就看得到。按錯可以復原。`
    : '';

  // Preserve the receipt until the owner prepares the next order or cancels this one.
  const orderBanner = counterOrder ? (
    <Box
      role="status" aria-label="門市訂單已建立"
      sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.25, px: 1.1, py: 0.9,
        bgcolor: C.goWash, border: '1px solid', borderColor: C.go, borderRadius: '10px',
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.go, minWidth: 0 }}>
        ✓ 已開門市訂單
        {counterOrder.id != null && (
          <Box component="span" sx={{ fontFamily: MONO, ml: '5px' }}>#{counterOrder.id}</Box>
        )}
        <Box component="span" sx={{ fontWeight: 700, color: C.ink2, ml: '6px' }}>
          {counterOrder.line_count} 筆 ／ {counterOrder.piece_count} 件 · 這些款已從下次建議扣掉
        </Box>
      </Typography>
      <Button
        // `busy` alone: the parent flips it in the same handler, and a local "undoing" flag would
        // stay stuck on if the cancel failed — leaving him with an order he cannot undo.
        size="small" variant="outlined" disabled={busy}
        onClick={onUndoOrder}
        sx={{ ml: 'auto', fontWeight: 800, flex: '0 0 auto' }}
      >
        復原
      </Button>
    </Box>
  ) : null;

  const actions = (
    <Box>
      {orderBanner}
      {orderUncertain && <Alert severity="warning">建立結果尚未確認。重試與複製會沿用原訂單，畫面上的新修改不會送入這次重試。</Alert>}
      <Button fullWidth variant="contained" disabled={busy || (!orderUncertain && orderPending) || (!orderUncertain && !counterOrder && orderedLines.length === 0)}
        onClick={counterOrder ? onNextOrder : onCreateOrder} sx={{ mb: 1 }}>
        {orderUncertain ? '重試確認原訂單' : counterOrder ? '準備下一張訂單' : '確認建立訂單'}
      </Button>
      <Box
        component="button" type="button" disabled={busy || !copyText}
        onClick={() => onCopy(copyText)}
        sx={{
          width: '100%', minHeight: 50, border: 0, borderRadius: '11px', cursor: 'pointer',
          bgcolor: C.back, color: '#fff', fontWeight: 800, fontSize: 15.5,
          boxShadow: '0 3px 10px rgba(191,66,24,.28)',
          '&:disabled': { opacity: 0.5, cursor: 'default' },
        }}
      >
        複製整張訂單
      </Box>
      {orderPromise && !counterOrder && (
        <Typography sx={{ fontSize: 12, color: C.ink3, mt: 0.6, lineHeight: 1.45 }}>
          {orderPromise}
        </Typography>
      )}
    </Box>
  );

  const strip = (
    <StatStrip
      cells={[
        { label: '下單日', value: monthDay(meta?.next_order_night), suffix: weekdayLabel(meta?.next_order_night) },
        { label: '韓國合計', value: koreaKept, suffix: '件', highlight: true },
        { label: '店家', value: groups.length, suffix: '家' },
        { label: `${monthDay(meta?.expected_arrival)} 到貨`, value: koreaKept, suffix: '件' },
      ]}
    />
  );

  return (
    // The phone's action bar is fixed to the bottom, so the page reserves its height. The 門市訂單
    // receipt makes that bar about a row taller — without this the 復原 button would sit on top of
    // the last card instead of below it.
    <Box sx={{ pb: desktop ? 2 : counterOrder || orderUncertain ? 29 : 20 }}>
      {strip}
      {/* Full width and above the split, on BOTH layouts: it accounts for the whole 選品 list, so
          it belongs to neither the store groups nor the order summary. It is also the first thing
          he looks for on this screen — he asked where the rest of his 清單 went three times before
          the screen could answer. Kept to one sentence + one wrapped chip row so a dense screen
          gains about two lines, not a panel. */}
      <OrderAccounting plan={plan} />
      <AssortmentOrderOverview plan={plan} />
      {desktop ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 65fr) minmax(0, 35fr)', gap: 2, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>{groupsBlock}</Box>
          <Box aria-label="訂單摘要" sx={{ position: 'sticky', top: 96, minWidth: 0 }}>
            {tierTiles}
            {selectionBar}
            <Typography sx={{ fontSize: 13, color: C.ink2, mb: 1 }}>
              下次下單 <b>{weekdayLabel(meta?.next_order_night)} {monthDay(meta?.next_order_night)} 晚</b>
              {' · 約 '}<b>{monthDay(meta?.expected_arrival)}</b> 到店
            </Typography>
            {laneShortLineNode}
            {asksLine}
            {actions}
            {warehouseLine && <Typography sx={{ fontSize: 12, color: C.ink3, mt: 1.5 }}>{warehouseLine}</Typography>}
            {whyBlock}
          </Box>
        </Box>
      ) : (
        <>
          {tierTiles}
          {laneShortLineNode}
          {selectionBar}
          {asksLine}
          {groupsBlock}
          {warehouseLine && <Typography sx={{ fontSize: 12, color: C.ink3, mb: 1.5 }}>{warehouseLine}</Typography>}
          {whyBlock}
          <Box
            sx={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, px: 1.5, pt: 1.25, pb: 1.5,
              background: `linear-gradient(to top, ${C.paper} 62%, rgba(244,239,230,0))`,
            }}
          >
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>{actions}</Box>
          </Box>
        </>
      )}

    </Box>
  );
};

export default OrderScreen;
